import { PROGRESS_EPOCH } from '../../shared/progressEpoch'
import { apiFetch } from '~/utils/api'
import {
  STARWEAVE_CACHE_KEY, STARWEAVE_LOCAL_KEY, STARWEAVE_PENDING_KEY, STARWEAVE_NODES,
  cost, emptyStarweaveAccount, ownedStarweaveIds, parseStarweaveRequest,
  refundableStardust, settleStarweave, validateStarweaveAccount,
  type StarweaveAccount, type StarweaveError, type StarweaveRequest, type StarweaveResponse,
} from '../../shared/starweave'

interface PendingPurchase { uid: string; request: StarweaveRequest }
export interface StarweaveActionResult { ok: boolean; amount?: number; error?: StarweaveError }
let identitySerial = 0
let inFlightLoad: { uid: string | null; promise: Promise<boolean> } | null = null

/** Account-owned talents. Cached production data is display/offline-run data only. */
export function useStarweave() {
  const wallet = useStardust()
  const auth = useAuthUser()
  const owner = useState<string | null>('starweave-owner', () => null)
  const account = useState<StarweaveAccount | null>('starweave-account', () => null)
  const loaded = useState('starweave-loaded', () => false)
  const loading = useState('starweave-loading', () => false)
  const verified = useState('starweave-verified', () => false)
  const busy = useState('starweave-busy', () => false)
  const error = useState<StarweaveError | null>('starweave-error', () => null)
  const pending = useState<PendingPurchase | null>('starweave-pending', () => null)
  const isCurrent = () => wallet.isLocalMode() || owner.value === auth.value?.uid
  const owned = computed(() => isCurrent() && account.value ? ownedStarweaveIds(account.value) : [])
  const revision = computed(() => isCurrent() ? account.value?.revision ?? 0 : 0)
  const refundable = computed(() => isCurrent() && account.value ? refundableStardust(account.value) : 0)

  function readMap(key: string): Record<string, unknown> {
    try {
      const stored = JSON.parse(localStorage.getItem(key) ?? 'null')
      return stored?.epoch === PROGRESS_EPOCH && stored.accounts && typeof stored.accounts === 'object'
        ? stored.accounts : {}
    } catch { return {} }
  }
  function storeForUid(key: string, uid: string, value: unknown) {
    const entries = readMap(key)
    // Own properties only, including for custom Firebase UIDs.
    Object.defineProperty(entries, uid, { value, enumerable: true, configurable: true, writable: true })
    localStorage.setItem(key, JSON.stringify({ epoch: PROGRESS_EPOCH, accounts: entries }))
  }
  function cachedForUid(key: string, uid: string): unknown {
    const entries = readMap(key)
    return Object.hasOwn(entries, uid) ? entries[uid] : null
  }
  function persistAccount(uid: string) {
    if (!account.value) return
    try { storeForUid(STARWEAVE_CACHE_KEY, uid, account.value) } catch { /* live server state remains valid */ }
  }
  function setPending(value: PendingPurchase | null, uid: string) {
    // Persist before dispatch; an uncertain response must retry the same request ID.
    storeForUid(STARWEAVE_PENDING_KEY, uid, value?.request ?? null)
    pending.value = value
  }

  /** Called synchronously by the auth listener, before any new-account loading. */
  function onIdentityChanged(uid: string | null) {
    if (wallet.isLocalMode()) return
    if (owner.value === uid && (loaded.value || account.value !== null)) return
    identitySerial++
    owner.value = uid
    account.value = null
    pending.value = null
    loaded.value = uid === null
    verified.value = false
    loading.value = false
    busy.value = false
    error.value = null
    if (!uid || typeof localStorage === 'undefined') return
    const cached = validateStarweaveAccount(cachedForUid(STARWEAVE_CACHE_KEY, uid))
    if (cached) { account.value = cached; loaded.value = true }
    const raw = cachedForUid(STARWEAVE_PENDING_KEY, uid) as { kind?: unknown } | null
    if (raw?.kind === 'buy' || raw?.kind === 'refund') {
      const request = parseStarweaveRequest(raw, raw.kind)
      if (request) pending.value = { uid, request }
    }
  }
  function stillCurrent(uid: string, serial: number) {
    return owner.value === uid && identitySerial === serial && auth.value?.uid === uid
  }
  function accept(response: StarweaveResponse, uid: string, serial: number): boolean {
    if (!stillCurrent(uid, serial)) return false
    const received = validateStarweaveAccount(response.account)
    if (!received || !Number.isSafeInteger(response.balance) || response.balance < 0) throw new Error('Invalid Starweave response')
    if (account.value?.generation === received.generation && account.value.revision > received.revision) return true
    account.value = received
    loaded.value = true
    verified.value = response.ok
    wallet.noteServerBalance(response.balance, uid)
    persistAccount(uid)
    return true
  }
  async function send(request: StarweaveRequest, user: { uid: string; getIdToken(): Promise<string> }, serial: number) {
    const token = await user.getIdToken()
    const integrityHeader = await appCheckHeader()
    if (!stillCurrent(user.uid, serial)) return { ok: false, error: 'conflict' as const }
    const response = await apiFetch<StarweaveResponse>(`/api/starweave/${request.kind}`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, ...integrityHeader },
      body: { ...request, epoch: PROGRESS_EPOCH },
    })
    if (!accept(response, user.uid, serial)) return { ok: false, error: 'conflict' as const }
    // A duplicate replay can arrive after a newer request has been started. It
    // must not delete that newer request's recovery record, including another tab's.
    if (pending.value?.uid === user.uid && pending.value.request.requestId === request.requestId) {
      const saved = cachedForUid(STARWEAVE_PENDING_KEY, user.uid) as Partial<StarweaveRequest> | null
      try {
        if (saved?.requestId === request.requestId) setPending(null, user.uid)
        else pending.value = null
      } catch { pending.value = null /* the server receipt still protects replay */ }
    }
    error.value = response.ok ? null : response.reason ?? 'offline'
    return { ok: response.ok, amount: response.amount, ...(error.value ? { error: error.value } : {}) }
  }

  /**
   * true = a fresh account read (or a bare guest/local test run). On an outage,
   * false preserves only this UID's cached ownership and loaded indicates whether
   * such a cache exists. No prototype keys, funds, or ownership are ever read.
   */
  async function load(): Promise<boolean> {
    if (!import.meta.client) return false
    wallet.load()
    if (wallet.isLocalMode()) {
      owner.value = 'local-wallet'
      let local: StarweaveAccount | null = null
      try { local = validateStarweaveAccount(JSON.parse(localStorage.getItem(STARWEAVE_LOCAL_KEY) ?? 'null')) } catch { /* empty test account */ }
      account.value = local ?? emptyStarweaveAccount('local-starweave-account')
      loaded.value = verified.value = true
      error.value = null
      return true
    }
    const uid = auth.value?.uid ?? null
    if (inFlightLoad?.uid === uid) return inFlightLoad.promise
    const job = (async () => {
      loading.value = true
      verified.value = false
      let serial = identitySerial
      try {
        const user = await wallet.getWalletUser(false)
        if ((user?.uid ?? null) !== (auth.value?.uid ?? null)) return false
        onIdentityChanged(user?.uid ?? null)
        serial = identitySerial
        if (!user) { loaded.value = true; error.value = null; return true }
        loading.value = true
        const token = await user.getIdToken()
        const response = await apiFetch<StarweaveResponse>('/api/starweave', {
          headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        })
        if (!accept(response, user.uid, serial)) return false
        error.value = response.ok ? null : response.reason ?? 'offline'
        if (response.ok && pending.value?.uid === user.uid) {
          const outcome = await send(pending.value.request, user, serial)
          return outcome.ok
        }
        return response.ok
      } catch {
        if (identitySerial === serial) { error.value = 'offline'; verified.value = false }
        return false
      } finally {
        if (identitySerial === serial) loading.value = false
      }
    })()
    inFlightLoad = { uid, promise: job }
    try { return await job } finally { if (inFlightLoad?.promise === job) inFlightLoad = null }
  }

  async function act(kind: 'buy' | 'refund', nodeId?: string): Promise<StarweaveActionResult> {
    if (busy.value) return { ok: false, error: 'busy' }
    busy.value = true
    error.value = null
    let serial = identitySerial
    try {
      wallet.load()
      if (wallet.isLocalMode()) {
        await load()
        const current = account.value!
        const node = STARWEAVE_NODES.find(value => value.id === nodeId)
        const request: StarweaveRequest = {
          kind, nodeId, requestId: crypto.randomUUID(), generation: current.generation,
          expectedRevision: current.revision,
          ...(kind === 'buy' ? { expectedPrice: node ? cost(node, ownedStarweaveIds(current)) : 1 } : {}),
        }
        const result = settleStarweave(current, wallet.balance.value, request)
        if (!result.ok) { error.value = result.reason ?? 'offline'; return { ok: false, error: error.value } }
        localStorage.setItem(STARWEAVE_LOCAL_KEY, JSON.stringify(result.account))
        if (!wallet.adjustLocalBalance(result.balance - wallet.balance.value)) {
          localStorage.setItem(STARWEAVE_LOCAL_KEY, JSON.stringify(current))
          return { ok: false, error: 'funds' }
        }
        account.value = result.account
        return { ok: true, amount: result.amount }
      }
      const user = await wallet.getWalletUser(true)
      if ((user?.uid ?? null) !== (auth.value?.uid ?? null)) return { ok: false, error: 'conflict' }
      if (!user) return { ok: false, error: 'signin' }
      onIdentityChanged(user.uid)
      serial = identitySerial
      busy.value = true
      // A pending request is always settled first, with its original ID. A new
      // action waits for the player to retry after that prior outcome is known.
      if (pending.value?.uid === user.uid) {
        const sameIntent = pending.value.request.kind === kind && pending.value.request.nodeId === nodeId
        const prior = await send(pending.value.request, user, serial)
        return sameIntent ? prior : { ok: false, error: 'conflict' }
      }
      if (!account.value || !loaded.value) {
        if (!await load() || !account.value) return { ok: false, error: error.value ?? 'offline' }
        serial = identitySerial
      }
      if (!stillCurrent(user.uid, serial)) return { ok: false, error: 'conflict' }
      await wallet.flush()
      if (!stillCurrent(user.uid, serial)) return { ok: false, error: 'conflict' }
      const current = account.value!
      const node = STARWEAVE_NODES.find(value => value.id === nodeId)
      const request: StarweaveRequest = {
        kind, requestId: crypto.randomUUID(), generation: current.generation, expectedRevision: current.revision,
        ...(kind === 'buy' ? { nodeId: nodeId ?? '', expectedPrice: node ? cost(node, ownedStarweaveIds(current)) : 1 } : {}),
      }
      setPending({ uid: user.uid, request }, user.uid)
      return await send(request, user, serial)
    } catch {
      if (identitySerial === serial) error.value = 'offline'
      return { ok: false, error: 'offline' }
    } finally { if (identitySerial === serial) busy.value = false }
  }

  function clear() {
    identitySerial++
    owner.value = null
    account.value = null
    pending.value = null
    loaded.value = verified.value = loading.value = busy.value = false
    error.value = null
  }

  return {
    owned, revision, refundable, loaded, loading, verified, busy, error,
    load, purchase: (nodeId: string) => act('buy', nodeId), refund: () => act('refund'),
    onIdentityChanged, clear,
  }
}
